// oxlint-disable-next-line no-restricted-imports
import { jest } from '@jest/globals';
import {
  act,
  type RenderHookResult,
  type RenderResult,
} from '@testing-library/react';
import { type PropsWithChildren, useEffect, useRef, ElementType } from 'react';

import { render } from '@dynatrace/strato-components-testing/jest';

export type RefCallback<T extends ElementType = 'div'> = (
  node: (T extends 'div' ? HTMLDivElement : HTMLElement) | null,
) => void;

type HookWithRefReturn<TResultValue> = RenderHookResult<TResultValue, unknown> &
  RenderResult;

type HookWithRefDummyProps<T extends ElementType> = PropsWithChildren<{
  as?: T;
}>;

export function renderHookWithRef<TValue, TAs extends ElementType = 'div'>(
  hook: () => [RefCallback<TAs>, TValue],
  { children, as }: HookWithRefDummyProps<TAs> = {},
) {
  type Result = {
    ref: (TAs extends 'div' ? HTMLDivElement : HTMLElement) | null;
    value: TValue;
  };

  const results: Result[] = [];
  const result = {
    get current() {
      // oxlint-disable-next-line typescript/no-non-null-assertion
      return results.at(-1)!;
    },
  };

  const Wrapper = ({ children, as }: HookWithRefDummyProps<TAs>) => {
    const ref = useRef<
      (TAs extends 'div' ? HTMLDivElement : HTMLElement) | null
    >(null);
    const [refCb, inViewport] = hook();

    useEffect(() => {
      results.push({
        ref: ref.current,
        value: inViewport,
      });
    });

    const Component = as ?? 'div';
    return (
      <Component
        ref={(node) => {
          ref.current = node as
            | (TAs extends 'div' ? HTMLDivElement : HTMLElement)
            | null;
          refCb(
            node as (TAs extends 'div' ? HTMLDivElement : HTMLElement) | null,
          );
        }}
      >
        {children}
      </Component>
    );
  };

  const renderResult = render(<Wrapper as={as}>{children}</Wrapper>);

  return {
    ...renderResult,
    result,
    rerender: ({ children, as }: HookWithRefDummyProps<TAs> = {}) =>
      renderResult.rerender(<Wrapper as={as}>{children}</Wrapper>),
  } as HookWithRefReturn<Result>;
}

export type MockIntersectionCallback = (
  entries: Partial<IntersectionObserverEntry>[],
  observer: Partial<IntersectionObserver>,
) => void;

type MockIntersectionOberserverReturn = {
  intersectionCb: MockIntersectionCallback;
  instance: {
    observe: ReturnType<typeof jest.fn>;
    unobserve: ReturnType<typeof jest.fn>;
    disconnect: ReturnType<typeof jest.fn>;
  };
  MockIntersectionObserver: typeof IntersectionObserver;
};

export function createMockIntersectionObserver(replaceGlobal: boolean = true) {
  const mockInstance = {
    root: null,
    rootMargin: '0px 0px 0px 0px',
    scrollMargin: '0px 0px 0px 0px',
    thresholds: [0.0],
    takeRecords: jest.fn(),
    observe: jest.fn(),
    unobserve: jest.fn(),
    disconnect: jest.fn(),
  };

  const MockIntersectionObserver =
    jest.fn<
      (
        callback: MockIntersectionCallback,
        _options: unknown,
      ) => typeof mockInstance
    >();

  const returnValue: MockIntersectionOberserverReturn = {
    intersectionCb: () => {
      throw new Error('Should be replaced by now');
    },
    instance: mockInstance,
    MockIntersectionObserver:
      MockIntersectionObserver as typeof IntersectionObserver,
  };

  MockIntersectionObserver.mockImplementation(
    (callback: MockIntersectionCallback, _options) => {
      returnValue.intersectionCb = (entries, observer) =>
        act(() => callback(entries, observer));
      return mockInstance;
    },
  );

  if (replaceGlobal) {
    window.IntersectionObserver =
      MockIntersectionObserver as typeof IntersectionObserver;
  }

  return returnValue;
}

export type MockMutationCallback = (
  mutations: Partial<MutationRecord>[],
  observer: MutationObserver,
) => void;

type MockMutationOberserverReturn = {
  mutationCb: MockMutationCallback;
  instance: {
    observe: ReturnType<typeof jest.fn<() => unknown>>;
    takeRecords: ReturnType<typeof jest.fn<() => MutationRecord[]>>;
    disconnect: ReturnType<typeof jest.fn<() => unknown>>;
  };
  MockMutationObserver: typeof MutationObserver;
};

export function createMockMutationObserver(replaceGlobal: boolean = true) {
  const mockInstance = {
    observe: jest.fn(),
    takeRecords: jest.fn<() => MutationRecord[]>(),
    disconnect: jest.fn(),
  };

  const MockMutationObserver =
    jest.fn<(callback: MockMutationCallback) => typeof mockInstance>();

  const returnValue: MockMutationOberserverReturn = {
    mutationCb: () => {
      throw new Error('Should be replaced by now');
    },
    instance: mockInstance,
    MockMutationObserver: MockMutationObserver as typeof MutationObserver,
  };

  MockMutationObserver.mockImplementation((callback: MockMutationCallback) => {
    returnValue.mutationCb = (mutations, observer) =>
      act(() => callback(mutations, observer));
    return mockInstance;
  });

  if (replaceGlobal) {
    window.MutationObserver = MockMutationObserver as typeof MutationObserver;
  }

  return returnValue;
}

export type MockResizeCallback = (
  entries: Partial<ResizeObserverEntry>[],
  observer: ResizeObserver,
) => void;

type MockResizeOberserverReturn = {
  resizeCb: MockResizeCallback;
  instance: {
    observe: ReturnType<typeof jest.fn>;
    unobserve: ReturnType<typeof jest.fn>;
    disconnect: ReturnType<typeof jest.fn>;
  };
  MockResizeObserver: typeof ResizeObserver;
};

export function createMockResizeObserver(replaceGlobal: boolean = true) {
  const mockInstance = {
    observe: jest.fn(),
    unobserve: jest.fn(),
    disconnect: jest.fn(),
  };

  const MockResizeObserver =
    jest.fn<(callback: MockResizeCallback) => typeof mockInstance>();

  const returnValue: MockResizeOberserverReturn = {
    resizeCb: () => {
      throw new Error('Should be replaced by now');
    },
    instance: mockInstance,
    MockResizeObserver: MockResizeObserver as typeof ResizeObserver,
  };

  MockResizeObserver.mockImplementation((callback: MockResizeCallback) => {
    returnValue.resizeCb = (entries, observer) =>
      act(() => callback(entries, observer));
    return mockInstance;
  });

  if (replaceGlobal) {
    window.ResizeObserver = MockResizeObserver as typeof ResizeObserver;
  }

  return returnValue;
}
